import Skeleton from '@/components/ui/Skeleton'
import {
  STAT_STRIP_SURFACE,
  STAT_STRIP_UNDIVIDED_GAP,
  statStripCellRules,
  statStripGrid,
  type StatStripSurface,
} from '@/components/ui/stat-strip-variants'
import { cn } from '@/lib/utils'

/** The value bar's own box, sized to each `size`'s real line-height (StatStrip.tsx's
 *  `VALUE_SIZE`) so the figure does not shift once it lands. */
const VALUE_BOX = {
  m: 'h-6',
  xl: 'h-7',
  hero: 'h-8',
} as const

const VALUE_BAR = {
  m: 'h-4 w-24',
  xl: 'h-5 w-28',
  hero: 'h-6 w-28',
} as const

interface StatStripSkeletonProps {
  /** How many cells the loaded strip will have, so the rules land where its rules will. */
  cells: number
  surface?: StatStripSurface
  /** Matches `StatStrip`'s own prop — see its comment. */
  divided?: boolean
  /** Matches `StatStrip`'s own prop and its default (`xl` on a `panel`) — see
   *  `VALUE_BOX`/`VALUE_BAR` above. */
  size?: keyof typeof VALUE_BOX
  /** Draws the line under each figure, for a strip whose cells carry `StatStrip`'s `sub`. */
  sub?: boolean
  className?: string | undefined
}

/**
 * The loading shape of a StatStrip: the same grid, rules and surface, with a label bar over an
 * `h-m` figure bar (and a sub-line bar, with `sub`), each centred in a box of the loaded line's height so the strip does not move
 * when the figures land. Aria-hidden like every Skeleton; the caller's status line announces the
 * wait.
 *
 * Its own light module (Skeleton, `cn` and the shared classes only): a route's `pendingComponent`
 * ships in the entry chunk, so it may not pull StatStrip's component module in.
 */
export default function StatStripSkeleton({
  cells,
  surface = 'none',
  divided = true,
  size = surface === 'panel' ? 'xl' : 'm',
  sub = false,
  className,
}: StatStripSkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={cn('@container min-w-0', STAT_STRIP_SURFACE[surface], className)}
    >
      <div
        className={cn(
          statStripGrid(surface),
          divided || surface === 'panel' ? null : STAT_STRIP_UNDIVIDED_GAP,
        )}
      >
        {Array.from({ length: cells }, (_, index) => (
          <div
            key={index}
            // Matches `StatCell`'s own `min-w-0` (Issue 16) — the loading shape shares the
            // loaded one's shrink behaviour, not just its size, so nothing jumps once data lands.
            className={cn(
              'flex min-w-0 flex-col gap-1',
              statStripCellRules(surface, index, cells, divided),
            )}
          >
            <div className="flex h-4 items-center">
              <Skeleton className="h-3 w-20" />
            </div>
            <div className={cn('flex items-center', VALUE_BOX[size])}>
              <Skeleton className={VALUE_BAR[size]} />
            </div>
            {sub ? (
              <div className="flex h-4 items-center">
                <Skeleton className="h-3 w-24 max-w-full" />
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  )
}
